/**
 * WHERE SHE CAME FROM, as decisions rather than as fields.
 *
 * `components/SourceFields.tsx` draws the three boxes; this file decides what they mean, and it is
 * separate from them because none of these rules needs a browser to be true. Priority 7 of the
 * business brief is knowing which channel is worth money, so every one of them is about not
 * writing a word nobody said and not erasing one somebody did.
 *
 *   THE CREDIT FOLLOWS THE WORD. Moving the channel off `referral` clears the introducer, because
 *   a household credited to somebody under a word that is not a referral is a payment nobody can
 *   explain. All three screens do it here, so they cannot do it three ways.
 *
 *   AN UNTOUCHED BOX IS NOT A CHANGE. Every one of these patches is audited, so a save that sends
 *   a column back unchanged writes a correction that corrects nothing, and a save that sends a
 *   column back EMPTY erases what the phone seam wrote. `firstAnswerPatch` exists for the second
 *   of those, which is the one that cost a source.
 */
import type { Channel, EnquiryState } from '@fk/shared';

/** The three columns that carry the answer, in the shape the screens hold them in while typing. */
export interface SourceAnswer {
  readonly sourceChannel: Channel | '';
  readonly sourceDetail: string;
  readonly referredByClientId: string | null;
}

/** What a row already says. Both patches take exactly these three names, so one helper fills them. */
export interface SourcePatch {
  sourceChannel?: Channel;
  sourceDetail?: string | null;
  referredByClientId?: string | null;
}

/** A row of either table read into the shape above. */
export function sourceAnswerOf(row: {
  readonly sourceChannel: Channel;
  readonly sourceDetail: string | null;
  readonly referredByClientId: string | null;
}): SourceAnswer {
  return {
    sourceChannel: row.sourceChannel,
    sourceDetail: row.sourceDetail ?? '',
    referredByClientId: row.referredByClientId,
  };
}

/**
 * Only what changed, so an untouched box never overwrites a column and the audit event carries a
 * correction rather than a restatement. An empty answer is not a patch at all: a channel is never
 * cleared, only replaced.
 */
export function sourcePatch(next: SourceAnswer, current: SourceAnswer): SourcePatch {
  const patch: SourcePatch = {};
  if (next.sourceChannel && next.sourceChannel !== current.sourceChannel) {
    patch.sourceChannel = next.sourceChannel;
  }
  const detail = next.sourceDetail.trim() || null;
  if (detail !== (current.sourceDetail.trim() || null)) patch.sourceDetail = detail;
  // The credit follows the word, in both directions: chosen on a referral, cleared off anything
  // else. A channel left unchosen is a form nobody has answered yet, and it touches neither.
  const introducer = next.sourceChannel === 'referral' ? next.referredByClientId : null;
  if (next.sourceChannel && introducer !== current.referredByClientId) {
    patch.referredByClientId = introducer;
  }
  return patch;
}

/* ------------------------------------------------------------------ *
 * The question a call leaves behind.
 * ------------------------------------------------------------------ */

/** What `api/src/seams/facts.ts` writes as the detail on the enquiry a call opens. */
export const INBOUND_CALL_DETAIL = 'inbound call';

/** Still being worked: everything that is not finished, which is what the list shows by default. */
export function openEnquiry(state: EnquiryState): boolean {
  return state !== 'booked' && state !== 'lost';
}

/**
 * Whether this row is one nobody has asked the question on: an enquiry a call opened, filed
 * `phone`, carrying the literal the phone seam writes or nothing at all. A row already carrying a
 * detail somebody typed, or any other channel, has its answer and is never asked again.
 */
export function nobodyHasAskedHer(row: {
  readonly sourceChannel: Channel;
  readonly sourceDetail: string | null;
}): boolean {
  if (row.sourceChannel !== 'phone') return false;
  const detail = row.sourceDetail?.trim().toLowerCase() ?? '';
  return detail === '' || detail === INBOUND_CALL_DETAIL;
}

/**
 * WHETHER TO ASK HER, and the answer is about the ROW rather than about the press.
 *
 * The first version of this asked only while the enquiry was still `new` with no first response
 * stamped, which reads like the moment of the first reply and is the one moment it never is:
 * `api/src/seams/facts.ts` moves an ANSWERED call to `contacted` and stamps `first_response_at` in
 * the same batch it handles `call.ended`. So the one enquiry where somebody actually had her on
 * the line arrived already past the gate, and the question was drawn on missed calls only.
 *
 * It is asked on any enquiry still being worked whose source nobody has answered, and answering it
 * is what ends it: a typed detail or any word other than `phone` means somebody has already said
 * where she came from, and `nobodyHasAskedHer` is false from then on.
 */
export function asksHowSheHeard(row: {
  readonly state: EnquiryState;
  readonly sourceChannel: Channel;
  readonly sourceDetail: string | null;
}): boolean {
  return openEnquiry(row.state) && nobodyHasAskedHer(row);
}

/**
 * WHAT "NOT ASKED" MEANS, and it has to mean nothing at all.
 *
 * The question is drawn with its channel select sitting on "Not asked", so the commonest press of
 * that form is a save by somebody who never touched it. `sourcePatch` read against an untouched
 * answer sends `sourceDetail: null`, because an empty box IS a cleared column everywhere else on
 * this screen, and on an enquiry a call opened that erases the `inbound call` the phone seam
 * wrote, in an audited write nobody asked for. So the chosen word is what opens the patch.
 *
 * A DETAIL TYPED UNDER NO WORD IS STILL KEPT, because words somebody typed are never thrown away
 * by a save. It is the one field that travels on its own; the channel and the credit never do.
 *
 * PREFILLING IT WOULD BE WORSE THAN LEAVING IT EMPTY. This form is only ever drawn on a row whose
 * detail is empty or the machine's own literal, so there is nothing of hers to prefill, and
 * carrying `inbound call` into the box would make it the detail of whatever channel she then
 * names.
 */
export function firstAnswerPatch(heard: SourceAnswer, current: SourceAnswer): SourcePatch {
  if (heard.sourceChannel !== '') return sourcePatch(heard, current);
  const detail = heard.sourceDetail.trim();
  return detail && detail !== current.sourceDetail.trim() ? { sourceDetail: detail } : {};
}
